'use client';

import type { TrackingEventName } from '@repo/lib/schemas/trpc/tracking-events';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useMemo } from 'react';
import { useUser } from '@/contexts/user-context';
import { captureAttribution, getAttributionContext } from '@/lib/attribution';
import { authClient } from '@/lib/auth-client';
import { trpc } from '@/lib/trpc';

function isProd() {
  return process.env.NODE_ENV === 'production';
}

type ConversionProps = {
  value?: number;
  currency?: string;
  eventId?: string;
  [key: string]: unknown;
};

type DataLayerEvent = Record<string, unknown>;

type TikTokQueue = {
  track: (event: string, props?: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
    ttq?: TikTokQueue;
  }
}

const pushToDataLayer = (evt: DataLayerEvent) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(evt);
  } catch (_e) {
    /* ignore */
  }
};

type WindowWithTrackingFlag = Window & {
  __mp_page_view_tracked?: boolean;
};

export function useAnalytics() {
  const { data: tracking } = trpc.config.getTracking.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const { data: session } = authClient.useSession();
  const { userProgress } = useUser();

  const hasMixpanel = Boolean(tracking?.mixpanelToken);
  const hasTikTok = Boolean(tracking?.tiktokPixelId);
  const canLoad = isProd();
  const userEmail = session?.user?.email ?? null;

  // Get stored ttclid from cookies
  const getTtclid = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'ttclid') {
        return value;
      }
    }
    return;
  }, []);

  useEffect(() => {
    if (!session?.user) {
      return;
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'user_identified',
        user_id: session.user.id,
        user_email: session.user.email,
      });
    } catch (_e) {
      /* ignore */
    }
  }, [session?.user]);

  // Initialize mutations
  const sendFacebookEventMutation = trpc.tracking.sendFacebookEvent.useMutation();
  const sendToGtmServerMutation = trpc.tracking.sendToGtmServer.useMutation();
  const sendTikTokEventMutation = trpc.tracking.sendTikTokEvent.useMutation();

  const identify = useCallback(
    (userId?: string, traits?: Record<string, unknown>) => {
      if (!(canLoad && hasMixpanel)) {
        return;
      }
      try {
        if (userId) {
          mixpanel.identify(userId);
        }
        if (traits) {
          mixpanel.register(traits);
        }
      } catch (_e) {
        /* ignore */
      }
    },
    [canLoad, hasMixpanel]
  );

  const pageView = useCallback(
    (view: string, params?: Record<string, unknown>) => {
      if (!canLoad) {
        return;
      }
      captureAttribution();
      const attribution = getAttributionContext();
      const attributionContext = Object.keys(attribution).reduce<Record<string, string>>(
        (acc, key) => {
          const value = attribution[key];
          if (value) {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );
      const baseProps = {
        view,
        path: typeof window !== 'undefined' ? window.location.pathname : undefined,
        referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
        ...attributionContext,
      };
      pushToDataLayer({ event: 'page_view', ...baseProps, ...(params || {}) });
      if (hasMixpanel) {
        try {
          const w = window as WindowWithTrackingFlag;
          if (w.__mp_page_view_tracked) {
            w.__mp_page_view_tracked = false;
            return;
          }
          mixpanel.track('page_view', { ...baseProps, ...(params || {}) });
        } catch (_e) {
          /* ignore */
        }
      }
    },
    [canLoad, hasMixpanel]
  );

  const track = useCallback(
    (event: string, props?: Record<string, unknown>) => {
      const isInternalUser =
        (session?.user?.email?.endsWith('@stardecide.com') ||
          (session?.user as { role?: string } | undefined)?.role === 'admin') &&
        process.env.NODE_ENV === 'production';
      if (isInternalUser) {
        return;
      }
      if (!canLoad) {
        return;
      }
      captureAttribution();
      const attribution = getAttributionContext();
      const attributionContext = Object.keys(attribution).reduce<Record<string, string>>(
        (acc, key) => {
          const value = attribution[key];
          if (value) {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );
      const mergedProps = { ...attributionContext, ...(props || {}) };
      pushToDataLayer({ event, ...mergedProps });
      if (hasMixpanel) {
        try {
          mixpanel.track(event, mergedProps);
        } catch (_e) {
          /* ignore */
        }
      }
      // TikTok browser tracking through analytics loader
      if (hasTikTok) {
        try {
          if (window.ttq) {
            window.ttq.track(event, { ...props, ttclid: getTtclid() });
          }
        } catch (_e) {
          /* ignore */
        }
      }
    },
    [canLoad, hasMixpanel, hasTikTok, session?.user, getTtclid]
  );

  const SERVER_ALLOWED = useMemo(
    // 'Purchase' is intentionally absent: the Stripe webhook is the only
    // trusted source of purchase conversions (server-verified amount).
    () => new Set<TrackingEventName>(['CompleteRegistration', 'StartTrial']),
    []
  );

  const alias = useCallback(
    (userId: string) => {
      if (!(canLoad && hasMixpanel)) {
        return;
      }
      try {
        mixpanel.alias(userId);
      } catch (_e) {
        /* ignore */
      }
    },
    [canLoad, hasMixpanel]
  );

  const forwardToFacebook = useCallback(
    (eventName: string, props?: ConversionProps) => {
      sendFacebookEventMutation.mutate({
        eventName,
        value: props?.value,
        currency: props?.currency,
        eventId: props?.eventId,
        email: userEmail || undefined,
        eventSourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        clientUserAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
    },
    [sendFacebookEventMutation, userEmail]
  );

  const forwardToTikTok = useCallback(
    (eventName: string, props?: ConversionProps) => {
      if (!tracking?.tiktokPixelId) {
        return;
      }
      const ttclid = getTtclid();
      const eventId =
        props?.eventId ||
        (typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`);

      sendTikTokEventMutation.mutate(
        {
          pixelCode: tracking.tiktokPixelId as string,
          event: eventName,
          eventId,
          context: {
            ad: ttclid ? { callback: ttclid } : undefined,
            page: {
              url: typeof window !== 'undefined' ? window.location.href : '',
            },
          },
          user: {
            email: userEmail || undefined,
          },
          properties: {
            value: props?.value,
            currency: props?.currency,
          },
        },
        {
          onError: (error) => {
            console.warn('[TikTok Server] Failed to send event:', error);
          },
          onSuccess: (result) => {
            if (result.ok === false && 'reason' in result) {
              console.warn('[TikTok Server] Event skipped:', result.reason);
            }
          },
        }
      );
    },
    [sendTikTokEventMutation, tracking?.tiktokPixelId, userEmail, getTtclid]
  );

  const forwardToGtmServer = useCallback(
    (eventName: string, props?: Record<string, unknown>) => {
      if (!tracking?.enableServerConversions) {
        return;
      }
      sendToGtmServerMutation.mutate({
        payload: { event: eventName, ...(props ?? {}) },
      });
    },
    [sendToGtmServerMutation, tracking?.enableServerConversions]
  );

  const sendServerConversion = useCallback(
    (name: string, props?: ConversionProps) => {
      if (!(canLoad && tracking?.enableServerConversions)) {
        return;
      }
      if (!SERVER_ALLOWED.has(name)) {
        return;
      }
      const payload: ConversionProps = { ...(props ?? {}) };
      if (!payload.eventId) {
        payload.eventId =
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;
      }
      forwardToFacebook(name, payload);
      forwardToTikTok(name, payload);
      forwardToGtmServer(name, { ...payload, event_id: payload.eventId });
    },
    [
      canLoad,
      tracking?.enableServerConversions,
      forwardToFacebook,
      forwardToTikTok,
      forwardToGtmServer,
      SERVER_ALLOWED,
    ]
  );

  useEffect(() => {
    if (!(session?.user && userProgress && hasMixpanel && canLoad)) {
      return;
    }
    try {
      mixpanel.register({
        'User Role': (session.user as { role?: string } | undefined)?.role,
        'Subscription Status': userProgress.subscriptionStatus,
        'Zodiac Sign': (userProgress as { sign?: string } | undefined)?.sign,
        'User Level': (userProgress as { level?: string | number } | undefined)?.level,
        'Cosmic Dust': (userProgress as { cosmicDust?: number } | undefined)?.cosmicDust,
      });
      mixpanel.identify(session.user.id);
      mixpanel.people.set({
        $email: session.user.email,
        $name: session.user.name,
        Plan: userProgress.subscriptionStatus,
        Sign: (userProgress as { sign?: string } | undefined)?.sign,
      });
    } catch (_e) {
      /* ignore */
    }
  }, [session?.user, userProgress, hasMixpanel, canLoad]);

  return useMemo(
    () => ({ identify, alias, pageView, track, sendServerConversion }),
    [identify, alias, pageView, track, sendServerConversion]
  );
}
